/* eslint complexity: "off" */
import React from 'react'
import styled from 'styled-components'
import { graphql, compose, withApollo } from 'react-apollo'
import moment from 'moment'
import FormOptions from './FormOptions'
import InterimControls from './InterimControls'
import PLDatePicker from './PLDatePicker'
import CacheUtil from '../common/utils/CacheUtil'
import CreateCashflowDatum from './queries/CreateCashflowDatum'
import CreateFiscalYear from './queries/CreateFiscalYear'
import FinancialReviewQuery from './queries/FinancialReviewQuery'
import SuccessNotification from '../common/SuccessNotification'
import UpdateFiscalYear from './queries/UpdateFiscalYear'

const Year = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-basis: 0;
  flex-grow: 1;
`
const Exclamation = styled.i`
  color: #dd6a01;
  margin-left: 3px;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`
const Small = styled.small`
  display: block;
  text-align: center;
  white-space: nowrap;
`
const DataSource = styled.small`
  display: block;
  text-align: center;
  white-space: nowrap;
  font-size: 70%;
`
const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`
const Title = styled.b`
  text-align: right;
  white-space: nowrap;
`

class PaneHeaderYear extends React.Component {
  constructor() {
    super()
    this.state = {
      isSuccessNotificationVisible: false,
      successNotificationText: ''
    }
  }
  flashSuccessNotification = text => {
    this.setState({ isSuccessNotificationVisible: true, successNotificationText: text })
    setTimeout(
      () => this.setState({ isSuccessNotificationVisible: false }),
      4000
    )
  }
  closeSuccessNotification = () => {
    this.setState({ isSuccessNotificationVisible: false })
  }
  onAddClick = async () => {
    const {
      createFiscalYear,
      updateFiscalYear,
      year,
      cashflowOptions,
      loanId,
      entity,
      loan,
      client,
      queriedYears,
      fiscalYearStartDate,
      resetYearSources
    } = this.props
    let days
    const dateInMonth = moment().date()
    let plEnd
    if (dateInMonth >= 15) {
      plEnd = moment().year(year + 1).subtract(1, 'month').endOf('month')
    } else {
      plEnd = moment().year(year + 1).subtract(2, 'month').endOf('month')
    }
    // If we don't add one day, the date will be off by one :(
    days = plEnd.diff(fiscalYearStartDate.clone().add(1, 'year'), 'd') + 1
    const nextYear = moment().year(year + 1).startOf('year')
    const entities = CacheUtil.getEntities(loan)
    const newApplicableYears = Array.from(new Array(5), (_, idx) => nextYear.year() - idx).reverse()
    const queryVariables = {
      years: queriedYears,
      cashflowOptions,
      loanId
    }
    for (const e of entities) {
      const fiscalYear = e.fiscalYears.find(fy => fy.year === year)
      const { isFullYear, isLeapYear, rid, period } = fiscalYear
      try {
        await createFiscalYear({
          variables: {
            entityType: e.__typename,
            entityId: e.rid,
            days,
            period: 'interim',
            year: nextYear.year()
          },
          update: (store, { data: { createFiscalYear: { fiscalYear } } }) => {
            const data = store.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
            const cachedEntity = CacheUtil.findEntityInQuery(e, data)
            cachedEntity.fiscalYears.unshift(fiscalYear) // Unshift because fiscal years are in descending order by year
            store.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data })
          }
        })
        // Need to recalculate the yearSources object on <Pane />
        // to consider the new fiscal year
      } catch (err) {
        console.log(err)
      }
      if (!isFullYear && period === 'interim') {
        // Convert earlier interim to full year if it is not already
        try {
          await updateFiscalYear({
            variables: {
              entityType: e.__typename,
              rid,
              days: isLeapYear ? 366 : 365
            },
            update: (store, { data: { updateFiscalYear: { fiscalYear } } }) => { // Note, fiscalYear here is the return value from the server, not the previously cached fiscalYear used earlier
              const data = store.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
              const cachedEntity = CacheUtil.findEntityInQuery(e, data)
              const idx = cachedEntity.fiscalYears.findIndex(fy => fy.year === year)
              cachedEntity.fiscalYears[idx] = fiscalYear
              store.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data })
            }
          })
        } catch (err) {
          console.log(err)
        }
      }
      resetYearSources()
    }
    const data = client.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
    const oc = CacheUtil.findEntityInQuery(entity, data)
    oc.applicableYears = newApplicableYears
    client.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data })
    this.flashSuccessNotification(`${nextYear.year()} interim has been successfully added`)
  }
  renderGuarantorTitle = () => {
    const { isAveraged, isEditable } = this.props
    /* Depending on whether or not FRT is editable, year + title
     * are on one or two lines. Therefore, a space is or is not required.
     */
    if (isAveraged) {
      return isEditable ? 'Average ' : 'Average'
    }
  }
  renderBusinessTitle = () => {
    const { isEditable, entity, year } = this.props
    const { fiscalYears } = entity
    const fiscalYear = fiscalYears.find(fy => fy.year === year)
    const { isFullYear, period } = fiscalYear
    if (period === 'interim' && !isFullYear && !isEditable) {
      return 'Annualized'
    }
  }
  onRemoveClick = () => {
    const { toggleRemoveInterimModal } = this.props
    toggleRemoveInterimModal()
  }
  renderSource = () => {
    const { data, year, resourceType, yearSources, fiscalYear } = this.props
    let title = ''
    const { period } = fiscalYear
    if (yearSources[year]) {
      const { source } = yearSources[year]
      if (period === 'interim' && resourceType === 'Business') {
        title = 'Company Prepared'
      } else if (period === 'interim' && resourceType === 'Guarantor') {
        return null
      } else if (data.some(datum => datum.year === year && datum.source === 'tax_transcript')) {
        title = 'Tax Transcripts'
      } else if (source === 'manual_transcript') {
        title = 'Manual Transcripts'
      } else if (source === 'manual_document') {
        title = 'Tax Returns'
      }
    }
    return (
      <DataSource>
        {title}
      </DataSource>
    )
  }
  renderDates = () => {
    const {
      pLUrl,
      period,
      isEditable,
      resourceType,
      fiscalYearStartDate,
      year,
      fiscalYear,
      pAndLEndDate,
      cashflowOptions,
      queriedYears,
      loan,
      entity
    } = this.props
    return (
      <PLDatePicker
        pLUrl={pLUrl}
        period={period}
        isEditable={isEditable}
        resourceType={resourceType}
        fiscalYearStartDate={fiscalYearStartDate}
        fiscalYear={fiscalYear}
        year={year}
        pAndLEndDate={pAndLEndDate}
        cashflowOptions={cashflowOptions}
        queriedYears={queriedYears}
        loan={loan}
        entity={entity}
      />
    )
  }
  renderInterimControls = () => {
    const { resourceType, entity, year, isEditable } = this.props
    return (
      <InterimControls
        resourceType={resourceType}
        entity={entity}
        year={year}
        isEditable={isEditable}
        onAdd={this.onAddClick}
        onRemove={this.onRemoveClick}
      />
    )
  }
  renderSuccessNotification = () => {
    const { isSuccessNotificationVisible, successNotificationText } = this.state
    if (isSuccessNotificationVisible) {
      return (
        <SuccessNotification onClose={this.closeSuccessNotification} duration={3} text={successNotificationText} />
      )
    }
  }
  render() {
    const {
      onFormOptionsChange,
      year,
      data,
      isEditable,
      hasDiscrepancy,
      resourceType,
      fiscalYear,
      entity,
      cashflowOptions,
      loan,
      changeYearSource,
      queriedYears,
      refetch
    } = this.props
    return (
      <Year key={`paneheader-${year}`}>
        <Title>
          {resourceType === 'Business' ? this.renderBusinessTitle() : this.renderGuarantorTitle()}
          {!isEditable && <br />}
          {year}
          {hasDiscrepancy && <Exclamation className="fa fa-exclamation-circle" />}
        </Title>
        <InfoContainer>
          <FormOptions
            onFormOptionsChange={onFormOptionsChange}
            cashflowOptions={cashflowOptions}
            fiscalYear={fiscalYear}
            resourceType={resourceType}
            entity={entity}
            loan={loan}
            changeYearSource={changeYearSource}
            data={data}
            queriedYears={queriedYears}
            isEditable={isEditable}
            refetch={refetch}
          />
          {this.renderDates()}
          {this.renderSource()}
        </InfoContainer>
        {this.renderInterimControls()}
        {this.renderSuccessNotification()}
      </Year>
    )
  }
}

export default compose(
  graphql(CreateCashflowDatum, {
    name: 'createCashflowDatum'
  }),
  graphql(CreateFiscalYear, {
    name: 'createFiscalYear'
  }),
  graphql(UpdateFiscalYear, {
    name: 'updateFiscalYear'
  }),
  withApollo
)(PaneHeaderYear)
